/**
 * The decisions behind the image viewer's touch gestures, kept as plain
 * functions so they can be tested without a phone. ImageLightbox only reads
 * pointer events and calls these.
 */

export const MAX_SCALE = 5;
export const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;
const SWIPE_PX = 60;

export interface Point {
  x: number;
  y: number;
}
export interface Size {
  width: number;
  height: number;
}
export interface Tap extends Point {
  time: number;
}
export type SwipeIntent = 'close' | 'next' | 'previous' | null;

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(1, scale));
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Fingers twice as far apart as at the start means twice the zoom. */
export function pinchScale(startScale: number, startDistance: number, nowDistance: number): number {
  if (startDistance <= 0) {
    return startScale;
  }
  return clampScale((startScale * nowDistance) / startDistance);
}

/** A zoomed picture can move only as far as its edges; a fitted one not at all. */
export function clampPan(pan: Point, scale: number, area: Size): Point {
  const limitX = Math.max(0, (area.width * (scale - 1)) / 2);
  const limitY = Math.max(0, (area.height * (scale - 1)) / 2);
  // "+ 0" turns a negative zero into zero.
  return {
    x: Math.max(-limitX, Math.min(limitX, pan.x)) + 0,
    y: Math.max(-limitY, Math.min(limitY, pan.y)) + 0,
  };
}

export function isDoubleTap(previous: Tap | null, next: Tap): boolean {
  return (
    previous !== null &&
    next.time - previous.time <= DOUBLE_TAP_MS &&
    distance(previous, next) <= DOUBLE_TAP_PX
  );
}

/** Zoomed in: back to fitted. Fitted: zoom in on the tapped point. */
export function doubleTapView(
  scale: number,
  tap: Point,
  area: Size,
): { scale: number; pan: Point } {
  if (scale > 1) {
    return { scale: 1, pan: { x: 0, y: 0 } };
  }
  const offsetX = tap.x - area.width / 2;
  const offsetY = tap.y - area.height / 2;
  return {
    scale: DOUBLE_TAP_SCALE,
    pan: clampPan(
      { x: 0 - offsetX * (DOUBLE_TAP_SCALE - 1), y: 0 - offsetY * (DOUBLE_TAP_SCALE - 1) },
      DOUBLE_TAP_SCALE,
      area,
    ),
  };
}

/** What a one-finger drag on a fitted picture means. Zoomed, it is a pan. */
export function swipeIntent(
  delta: { dx: number; dy: number },
  scale: number,
  hasSiblings: boolean,
): SwipeIntent {
  if (scale > 1) {
    return null;
  }
  const { dx, dy } = delta;
  if (dy >= SWIPE_PX && dy > 1.5 * Math.abs(dx)) {
    return 'close';
  }
  if (hasSiblings && Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > 1.5 * Math.abs(dy)) {
    return dx < 0 ? 'next' : 'previous';
  }
  return null;
}
